import { z } from 'zod';

const id = z.string().min(1).max(256);
const date = z.string().datetime({ offset: true });
const actor = z.object({ id, name: z.string() });
const url = z.string().url().refine(value => {
  const parsed = new URL(value);
  return parsed.protocol === 'https:' && !parsed.username && !parsed.password && !parsed.port
    && (parsed.hostname === 'monday.com' || parsed.hostname.endsWith('.monday.com'));
}, 'Expected a Monday.com URL');

export const MondayBoardIdsSchema = z.array(id).max(25)
  .refine(ids => new Set(ids).size === ids.length, 'Select each Monday.com board once');
export const MondayBoardSchema = z.object({
  id, name: z.string(), state: z.enum(['active', 'archived', 'deleted']),
  hierarchy_type: z.enum(['classic', 'multi_level']),
  workspace_id: id.nullable(), url,
});

const column = z.object({ id, type: z.string(), text: z.string().nullable(), column: z.object({ title: z.string() }) });
export const MondayItemColumnSchema = z.discriminatedUnion('__typename', [
  column.extend({ __typename: z.literal('StatusValue'), label: z.string().nullable(), is_done: z.boolean().nullable() }),
  column.extend({ __typename: z.literal('BatteryValue'), is_leaf: z.boolean(), battery_value: z.array(z.object({ key: id, count: z.number().int().nonnegative() })).max(1000) }),
  column.extend({ __typename: z.literal('PeopleValue'), persons_and_teams: z.array(z.object({ id, kind: z.enum(['person', 'team', 'agent']).nullable() })).max(10_000).nullable() }),
  column.extend({ __typename: z.literal('TextValue') }),
  column.extend({ __typename: z.literal('LongTextValue') }),
]);
export const MondayItemSchema = z.object({
  id, name: z.string(), state: z.enum(['active', 'archived', 'deleted']),
  created_at: date, updated_at: date, url,
  board: z.object({ id }), parent_item: z.object({ id }).nullable(),
  group: z.object({ id, title: z.string() }).nullable(),
  column_values: z.array(MondayItemColumnSchema).max(1000),
});
export const MondayReplySchema = z.object({
  id, text_body: z.string().nullable(), created_at: date.nullable(), updated_at: date.nullable(),
  creator: actor.nullable(),
});
export const MondayUpdateSchema = MondayReplySchema.extend({
  item_id: id, replies: z.array(MondayReplySchema).max(10_000),
});
export const MondayCollectedItemSchema = z.object({
  providerAccount: id, selectedBoard: MondayBoardSchema, item: MondayItemSchema,
  updates: z.array(MondayUpdateSchema).max(10_000), collectedAt: date,
});
export type MondayBoard = z.infer<typeof MondayBoardSchema>;
export type MondayItem = z.infer<typeof MondayItemSchema>;
export type MondayUpdate = z.infer<typeof MondayUpdateSchema>;
export type MondayCollectedItem = z.infer<typeof MondayCollectedItemSchema>;
export interface MondayItemRecord {
  sourceId: string;
  snapshot: MondayCollectedItem;
}
